import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessMessage } from 'redux/modules/successMessage';
import moment from 'moment';
import { currencySymbol } from 'helpers/HelperFunctions';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Price from 'components/Price/Price';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import Label from 'components/Label/Label';
import Markdown from 'components/Shared/Markdown';

import styles from './CreativeSubmittedPitch.module.scss';
import { setErrorMessage } from 'redux/modules/errorMessage';

const PitchStatus = Object.freeze({
  BLOCKED: Symbol('blocked'),
  SUBMITTED_TO_TWINE: Symbol('submitted_to_twine'),
  SHARED_WITH_CLIENT: Symbol('shared_with_client'),
  PITCH_READ: Symbol('pitch_read'),
  DECLINED: Symbol('declined'),
  SHORTLISTED: Symbol('shortlisted'),
  ACCEPTED: Symbol('accepted'),
  COMPLETED: Symbol('completed'),
});

const CreativeSubmittedPitch = ({
  isPaidBrief,
  contactOptions,
  currency,
  editing,
  editingPitchAmount,
  editingPitchText,
  onChangePitchAmount,
  onChangePitchText,
  onEdit,
  onEditSave,
  onEditCancel,
  onPitchAgain,
  onResendContactDetails,
  owner,
  pitch,
  pitchError,
  savingEdit,
  shouldOpenEditing,
  user,
}) => {
  const dispatch = useDispatch();
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    resendingContactDetails,
    resendContactDetailsSuccess,
    resendContactDetailsError,
  } = useSelector((state) => state.briefDetails);

  // Check for existing cooldown on component mount
  useEffect(() => {
    const lastResendTime = localStorage.getItem('contactDetailsLastResend');
    if (lastResendTime) {
      const remainingCooldown = Math.max(
        0,
        60 - Math.floor((Date.now() - parseInt(lastResendTime)) / 1000)
      );

      if (remainingCooldown > 0) {
        setResendCooldown(remainingCooldown);
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            const newValue = prev - 1;
            if (newValue <= 0) {
              clearInterval(timer);
              localStorage.removeItem('contactDetailsLastResend');
            }
            return Math.max(0, newValue);
          });
        }, 1000);
      } else {
        localStorage.removeItem('contactDetailsLastResend');
      }
    }
  }, []);

  useEffect(() => {
    if (!resendingContactDetails && resendContactDetailsSuccess) {
      dispatch(
        setSuccessMessage(
          'Contact details will be available in your messages within a few minutes'
        )
      );
    }

    if (!resendingContactDetails && resendContactDetailsError) {
      dispatch(
        setErrorMessage(
          'There was an error re-requesting the client details. Please try again later.'
        )
      );
    }
  }, [
    resendingContactDetails,
    resendContactDetailsSuccess,
    resendContactDetailsError,
  ]);

  const handleResendContactDetails = () => {
    if (resendCooldown > 0) return;

    onResendContactDetails();
    localStorage.setItem('contactDetailsLastResend', Date.now().toString());
    setResendCooldown(60);

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          clearInterval(timer);
          localStorage.removeItem('contactDetailsLastResend');
        }
        return Math.max(0, newValue);
      });
    }, 1000);
  };

  // Set the pitch status
  let pitchState = PitchStatus.SUBMITTED_TO_TWINE;
  let pitchLabelColour = 'transparent';
  let pitchLabelTooltip =
    'Your application has been submitted to Twine and is awaiting review';
  let pitchLabelText = 'Submitted ⏳';

  switch (true) {
    case pitch.blocked_at !== '':
      pitchState = PitchStatus.BLOCKED;
      pitchLabelText = 'Not approved 🛑';
      pitchLabelTooltip =
        'Your application has not been sent to the client. ' +
        (pitch.blocked_reason == 'wrong_location'
          ? 'Your location does not match the location of the job'
          : pitch.blocked_reason == 'spam'
          ? 'We determined your application is spam'
          : pitch.blocked_reason == 'public_question'
          ? 'For more information about a job, ask a public question'
          : pitch.blocked_reason == 'no_relevant_work'
          ? 'Your portfolio must be relevant to this job'
          : pitch.blocked_reason == 'not_enough_info'
          ? 'You need to explain why you are right for the job'
          : pitch.blocked_reason == 'using_template'
          ? 'Your application must be personalised to the job'
          : pitch.blocked_reason == 'using_external_links'
          ? 'We do not allow links to competitor platforms'
          : pitch.blocked_reason == 'not_level_project'
          ? 'Your experience level does not match the level specified in the job'
          : pitch.blocked_reason == 'brief_deleted'
          ? 'Unfortunately, the client has taken down this job'
          : '');
      break;
    case pitch.completed_at !== '':
      pitchState = PitchStatus.COMPLETED;
      pitchLabelText = 'Job complete 🎉';
      pitchLabelColour = 'success';
      pitchLabelTooltip =
        'The job is now complete. Thank you for your hard work!';
      break;
    case pitch.accepted_at !== '':
      pitchState = PitchStatus.ACCEPTED;
      pitchLabelText = 'Hired ✅';
      pitchLabelColour = 'success';
      pitchLabelTooltip = 'Great news! You have been hired for the project';
      break;
    case pitch.declined_at !== '':
      pitchState = PitchStatus.DECLINED;
      pitchLabelText = 'Declined ❌';
      pitchLabelColour = 'danger';
      pitchLabelTooltip =
        'Unfortunately, the client has decided not to move forward with your application';
      break;
    case pitch.liked_at !== '':
      pitchState = PitchStatus.SHORTLISTED;
      pitchLabelText = 'Shortlisted 📋';
      pitchLabelColour = 'purple';
      pitchLabelTooltip =
        'Congratulations! Your application has been shortlisted by the client';
      break;
    case pitch.receiver_viewed !== '':
      pitchState = PitchStatus.PITCH_READ;
      pitchLabelText = 'Application read 👀';
      pitchLabelColour = 'primary';
      pitchLabelTooltip = 'The client has read your application';
      break;
    case pitch.approved_at !== '':
      pitchState = PitchStatus.SHARED_WITH_CLIENT;
      pitchLabelText = 'Sent to Client ✉️';
      pitchLabelColour = 'primary';
      pitchLabelTooltip =
        'Your application has been reviewed and shared with the client for consideration';
      break;
  }

  const hasNoContactOptions = Object.values(contactOptions).every(
    (v) => v === false
  );
  const { email, phone, sms } = contactOptions;

  useEffect(() => {
    shouldOpenEditing && onEdit();
  }, []);

  return (
    <div className={`${styles.container}`}>
      <div>
        <div>
          <p className={styles.heading}>What happens now?</p>
        </div>
        <div className={styles.body}>
          <ul>
            <li className={styles.item}>
              {!user.auto_approve_pitch_flag ? (
                pitchState == PitchStatus.SUBMITTED_TO_TWINE ? (
                  'Twine will review your application before sending it to the client.'
                ) : (
                  'Your application has been sent to the client via Twine and email.'
                )
              ) : (
                <>
                  Clients details will be in{' '}
                  <a href="/messages">your messages</a> once this pitch is
                  approved.
                </>
              )}
            </li>
            {hasNoContactOptions ? (
              <li className={styles.item}>
                Client wants to be contacted privately on Twine.
              </li>
            ) : (
              <li className={styles.item}>
                Client wants to be contacted by:
                {email && (
                  <span className={styles.contactOption}>📧 Email</span>
                )}
                {sms && (
                  <span className={styles.contactOption}>
                    📱 WhatsApp / SMS
                  </span>
                )}
                {phone && (
                  <span className={styles.contactOption}>📞 Phone</span>
                )}
              </li>
            )}
            {user &&
              user.pro &&
              !hasNoContactOptions &&
              pitch.approved_at === '' &&
              !user.auto_approve_pitch_flag && (
                <li className={styles.item}>
                  Clients details will be in{' '}
                  <a href="/messages">your messages</a> once this pitch is
                  approved.
                </li>
              )}
            {user &&
              user.pro &&
              !hasNoContactOptions &&
              pitchState === PitchStatus.SHARED_WITH_CLIENT &&
              !user.auto_approve_pitch_flag && (
                <li className={styles.item}>
                  Clients details can be found in{' '}
                  <a href={`/messages/${owner.username}`}>your messages</a>.
                </li>
              )}
            {user && !user.pro && (
              <li className={styles.item}>
                Only <a href="/pricing/creative">Twine Pro</a> members get
                direct client contact details.
              </li>
            )}
            {pitch.declined_at === '' && !user?.pro && (
              <li className={styles.item}>
                You can{' '}
                <a href={`/messages/${owner.username}`}>send another message</a>
                {` to the client if you don't hear back in 3 days.`}
              </li>
            )}
            {pitch.declined_at === '' && user?.pro && (
              <li className={styles.item}>
                <a href="#" onClick={handleResendContactDetails}>
                  Re-request the client details
                </a>
                {' if they are not in your messages.'}
              </li>
            )}
            {pitchState === PitchStatus.DECLINED && (
              <li className={styles.item}>
                This client may now be working with another freelancer
              </li>
            )}
            {pitchState === PitchStatus.SHARED_WITH_CLIENT && (
              <li className={styles.item}>
                Look out for messages from the client asking more questions
              </li>
            )}
          </ul>
          {user &&
            user.pro &&
            (pitchState === PitchStatus.SHARED_WITH_CLIENT ||
              user.auto_approve_pitch_flag) && (
              <div className={styles.buttonContainer}>
                <Button href={`/messages/${owner.username}`} bsStyle="primary">
                  Go to Messages
                </Button>
              </div>
            )}
        </div>
      </div>

      <div className={styles.pitchStatusMobile}>
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip
              id="tooltip"
              style={{ fontSize: '14px', fontFamily: 'Lato' }}
            >
              {pitchLabelTooltip}
            </Tooltip>
          }
        >
          <Label
            color={pitchLabelColour}
            className={`${styles.pitchStatusPill}`}
          >
            {pitchLabelText}
          </Label>
        </OverlayTrigger>
      </div>
      <div className={styles.headerContainer}>
        <Fragment className={styles.pitchInfo}>
          <div className={styles.avatar}>
            <Thumbnail
              thumbnails={user.avatars}
              circle
              className={styles.avatarPic}
            />
          </div>
          <div className={styles.pitchTitle}>
            <div className={styles.title}>
              <strong>Your application:</strong>
            </div>
            <div className={styles.date}>
              Sent on {moment(pitch.timestamp).format('Do MMM YYYY')}
            </div>
          </div>
        </Fragment>
        <div className={styles.pitchStatus}>
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip
                id="tooltip"
                style={{ fontSize: '14px', fontFamily: 'Lato' }}
              >
                {pitchLabelTooltip}
              </Tooltip>
            }
          >
            <Label
              color={pitchLabelColour}
              className={`${styles.pitchStatusPill}`}
            >
              {pitchLabelText}
            </Label>
          </OverlayTrigger>
        </div>
      </div>
      <div
        className={styles.pitchText}
        style={{
          borderBottomLeftRadius: isPaidBrief && pitch.budget != 0 ? 0 : '4px',
          borderBottomRightRadius: isPaidBrief && pitch.budget != 0 ? 0 : '4px',
          marginBottom: isPaidBrief && pitch.budget != 0 ? 0 : '20px',
        }}
      >
        {editing ? (
          <div>
            <textarea
              className={`form-control`}
              disabled={savingEdit}
              onChange={(event) => onChangePitchText(event.target.value)}
              placeholder="Enter your pitch here"
              rows="4"
              value={editingPitchText}
            />
            {pitchError && pitchError.pitchText && (
              <div className={styles.errorMsg}>{pitchError.pitchText}</div>
            )}
          </div>
        ) : (
          <div>
            <Markdown text={pitch.text} target="_blank" />
          </div>
        )}
      </div>
      {isPaidBrief && (editing || pitch.budget != 0) && (
        <div className={styles.pitchDetails}>
          <div
            className={styles.label}
            style={{
              fontWeight: editing ? 'normal' : 'bold',
              lineHeight: editing ? '34px' : '23px',
            }}
          >
            Your quote:{' '}
          </div>
          {editing ? (
            <div className={styles.editingInput}>
              <input
                className={`form-control ${styles.amountInput} ${
                  pitchError && pitchError.pitchAmount && styles.error
                }`}
                disabled={savingEdit}
                onChange={(event) => onChangePitchAmount(event.target.value)}
                placeholder="Eg: 250"
                type="number"
                value={editingPitchAmount}
              />
              <div className={styles.currencySymbol}>
                {currencySymbol(currency)}
              </div>
              {pitchError && pitchError.pitchAmount && (
                <div className={styles.errorMsg}>{pitchError.pitchAmount}</div>
              )}
              <span className="aside-text fs-14">
                {`Set to ${currencySymbol(currency)}0 if you're still discussing
                with the client`}
              </span>
            </div>
          ) : (
            <strong>
              <Price currency={currency} amount={pitch.budget} />
            </strong>
          )}
          <br />
        </div>
      )}
      {editing && (
        <div className={styles.editingButtons}>
          <Button bsStyle="primary" disabled={savingEdit} onClick={onEditSave}>
            {savingEdit ? <em>saving...</em> : 'Save'}
          </Button>
          <Button bsStyle="info" disabled={savingEdit} onClick={onEditCancel}>
            Cancel
          </Button>
        </div>
      )}

      {pitch.blocked_at !== '' && pitch.blocked_reason !== 'spam' && (
        <Button
          bsStyle="info"
          className={styles.pitchAgainButton}
          onClick={onPitchAgain}
        >
          Re-apply
        </Button>
      )}

      {moment().diff(pitch.timestamp, 'minutes') > 5 &&
        pitch.blocked_at === '' &&
        !editing && (
          <Button
            bsStyle="info"
            className={styles.pitchAgainButton}
            onClick={onEdit}
          >
            Update application
          </Button>
        )}
    </div>
  );
};

CreativeSubmittedPitch.propTypes = {
  isPaidBrief: PropTypes.bool,
  contactOptions: PropTypes.object,
  currency: PropTypes.string,
  editing: PropTypes.bool,
  editingPitchAmount: PropTypes.object,
  editingPitchText: PropTypes.string,
  onChangePitchAmount: PropTypes.func,
  onChangePitchText: PropTypes.func,
  onEdit: PropTypes.func,
  onEditSave: PropTypes.func,
  onEditCancel: PropTypes.func,
  onPitchAgain: PropTypes.func,
  onResendContactDetails: PropTypes.func,
  owner: PropTypes.object,
  pitch: PropTypes.object,
  pitchError: PropTypes.object,
  savingEdit: PropTypes.bool,
  shouldOpenEditing: PropTypes.undefined,
  user: PropTypes.object,
};

export default CreativeSubmittedPitch;
